import { SlotGame } from "@nolimitcity/slot-game/bin/SlotGame";
import { FlowStateController } from "@nolimitcity/slot-game/bin/flowcontrol/FlowStateController";
import { gsap } from "gsap";

import {
    EventsManager,
    FlowEvents,
    ReelsEvents,
    WinEvents,
} from "../../../abstracts/events/EventsManager";
import { AbstractFlowPresentation } from "../../../abstracts/flow/AbstractFlowStatePresentation";
import { AbstractGameMode } from "../../../abstracts/flow/AbstractGameMode";
import { flat } from "../../../abstracts/utils/ArrayUtils";
import { fadeAndStopSound } from "../../../abstracts/utils/SoundUtils";
import { Apocalypse } from "../../Apocalypse";
import { ApocalypseSoundNames } from "../../assetsLoader/ApocalypseAssetsNames";
import { getWinLevel } from "../../config/ApocalypseConfig";
import { SHOW_FS_BILLBOARD, UPDATE_SECOND_STAGE_FS_LEFT } from "../../events/ApocalypseEvents";
import { ApocalypseReelsView } from "../../modules/Reels/ApocalypseReelsView";
import { ApocalypseFsContainer } from "../../modules/Reels/fs/ApocalypseFsContainer";
import { ApocalypseFsReel } from "../../modules/Reels/fs/ApocalypseFsReel";
import { ApocalypseWinView } from "../../modules/Winnings/ApocalypseWinView";
import { ApocalypseCounter } from "../../utils/ApocalypseCounter";
import { addExtraSpin } from "../decorators/ApocalypseFsFirstStageDecorator";

export class ApocalypseFreespinIdlePresentation extends AbstractFlowPresentation {
    protected winView: ApocalypseWinView;
    protected reelsView: ApocalypseReelsView;
    protected fsReels: ApocalypseFsReel[];
    protected fsContainer: ApocalypseFsContainer;
    protected fsLeftCounter: ApocalypseCounter;
    protected fsMultiplierCounter: ApocalypseCounter;

    constructor(id: string = `ApocalypseFreespinIdlePresentation`) {
        super(id);
    }

    protected handleEvents() {
        super.handleEvents();

        EventsManager.on(WinEvents.GET_WINNINGS_VIEW, (winningsView: ApocalypseWinView) => {
            this.winView = winningsView;
        });

        EventsManager.on(ReelsEvents.GET_REELS_VIEW, (reelsView: ApocalypseReelsView) => {
            this.reelsView = reelsView;
            this.fsReels = this.reelsView.getFreespinReels();
            this.fsContainer = this.reelsView.getFreespinContainer();
            this.fsLeftCounter = this.fsContainer.fsLeftContainer.counter;
            this.fsMultiplierCounter = this.fsContainer.fsMultiplierContainer.counter;
        });
    }

    protected get gameMode(): AbstractGameMode {
        // ! ce-i asta?!
        return AbstractGameMode.FREESPIN;
    }

    protected onIntroScreenComplete(fsLeft: number) {
        this.showFastSpinButton();

        this.keypad.setWin(
            this.model.data.accumulatedRoundWin,
            true,
            Apocalypse.model.isWinBelowStake()
        );

        // if(Apocalypse.model.data.isRestoreState)

        this.finishPresentation();
    }

    protected finishController() {
        SlotGame.apiPlugin.betHandler.zeroBet();
        super.finishController();
    }

    public override start(flowStateController: FlowStateController) {
        super.start(flowStateController);

        const isFirstStage = Apocalypse.model.isFirstStage;

        EventsManager.trigger(FlowEvents.FREESPIN_STAGES, isFirstStage);
    }

    protected override onRestoreStart() {
        super.onRestoreStart();
        this.hideFastSpinButton();

        let fsLeft = this.model.data.firstStage
            ? this.model.data.freespinsLeft
            : this.model.data.secondStageFsLeft;

        this.fsLeftCounter.getUpdateCounterTextAnimation(fsLeft, true);

        EventsManager.trigger(SHOW_FS_BILLBOARD);

        const tl = gsap.timeline();

        tl.add(this.fsContainer.revealSecondStageReels(true));

        if (
            Apocalypse.model.data.secondStage ||
            (Apocalypse.model.data.firstStage && Apocalypse.model.data.freespinsLeft === 0)
        ) {
            tl.add(this.fsContainer.unlockSecondStageReels(true));
        } else if (Apocalypse.model.data.firstStage) {
            EventsManager.trigger(UPDATE_SECOND_STAGE_FS_LEFT);
        }

        this.winView.showFreespinRestoreScreen(
            () => {
                const fsRestoreTl = gsap.timeline({
                    autoRemoveChildren: true,
                    smoothChildTiming: true,
                    // onStart: () => {
                    //     gsap.delayedCall(0.4, () => {
                    //     });
                    // },
                    onComplete: () => {
                        this.onIntroScreenComplete(fsLeft);
                    },
                });

                fsRestoreTl.add(this.winView.openRestoreScreenDoors());
                fsRestoreTl.call(() => {
                    this.keypad.setZeroBetSpinCounter(fsLeft);
                });

                if (this.model.data.mode === AbstractGameMode.NORMAL) {
                    fsRestoreTl.add(this.fsContainer.removeFakeScatters(), `>`);

                    const winValue = flat(this.model.data.winMatrixFS).reduce(
                        (accumulator, currentValue) => accumulator + currentValue,
                        0
                    );

                    const winLevel = getWinLevel(winValue);

                    fsRestoreTl.add(
                        this.fsMultiplierCounter.getUpdateCounterTextAnimation(
                            winValue,
                            false,
                            true,
                            winLevel.animationDuration / 6
                        ),
                        `>-=0.5`
                    );
                } else {
                    fsRestoreTl.add(this.fsContainer.movePersistentReels(), `>+=0.3`);
                    fsRestoreTl.add(
                        this.fsMultiplierCounter.getUpdateCounterTextAnimation(
                            Apocalypse.model.initData.totalFSWinning,
                            true
                        ),
                        0
                    );
                }
            },
            fsLeft,
            this.gameMode
        );

        const isWinBeloWStake = Apocalypse.model.isWinBelowStake(true);

        this.keypad.setWin(
            isWinBeloWStake ? -1 : this.model.initData.accumulatedRoundWin,
            true,
            Apocalypse.model.isWinBelowStake(isWinBeloWStake)
        );
    }

    protected onDefaultStart() {
        super.onDefaultStart();

        if (
            (this.model.data.mode !== AbstractGameMode.FREESPIN &&
                this.model.data.nextMode === AbstractGameMode.FREESPIN) ||
            (this.model.data.mode !== AbstractGameMode.SUPER_FREESPIN &&
                this.model.data.nextMode === AbstractGameMode.SUPER_FREESPIN) ||
            (this.model.data.mode !== AbstractGameMode.FREESPIN_BETTER &&
                this.model.data.nextMode === AbstractGameMode.FREESPIN_BETTER)
        ) {
            EventsManager.trigger(SHOW_FS_BILLBOARD);

            this.keypad.setWin(-1, true, true, true);
            this.hideFastSpinButton();

            fadeAndStopSound({
                name: ApocalypseSoundNames.MAIN_MUSIC,
                duration: 0.5,
                from: 1,
                to: 0,
            });

            const onComplete = () => {
                this.onIntroScreenComplete(this.model.data.freespinsLeft);
            };

            this.initializeTimeline(onComplete);

            // ! astea-s la fel
            this.flowPresentationTimeline.add(
                this.fsLeftCounter.getUpdateCounterTextAnimation(
                    this.model.data.freespinsLeft,
                    true
                )
            );

            // ! astea-s la fel

            this.flowPresentationTimeline.add(this.winView.openIntroScreenDoors(), `<`);

            this.flowPresentationTimeline.add(this.fsContainer.revealSecondStageReels(), `>`);

            this.flowPresentationTimeline.add(this.fsContainer.removeFakeScatters(), `>`);

            const winValue = flat(this.model.data.winMatrixFS).reduce(
                (accumulator, currentValue) => accumulator + currentValue,
                0
            );

            const winLevel = getWinLevel(winValue);

            this.flowPresentationTimeline.add(
                this.fsMultiplierCounter.getUpdateCounterTextAnimation(
                    winValue,
                    false,
                    true,
                    winLevel.animationDuration / 6
                ),
                `>-=0.5`
            );

            // this.flowPresentationTimeline.call(
            //     () => {
            //         this.keypad.setZeroBetSpinCounter(this.model.data.secondStageFsLeft);
            //     },
            //     undefined,
            //     `>`
            // );

            if (Apocalypse.model.data.firstStage) {
                EventsManager.trigger(UPDATE_SECOND_STAGE_FS_LEFT);
            }
        } else {
            if (Apocalypse.model.data.firstStage && Apocalypse.model.data.freespinsLeft === 0) {
                let extraSpinsWon =
                    this.model.data.secondStageFsLeft -
                    Apocalypse.model.data.fullReels.filter((reel) => reel).length;
                const zeroBetSpins = extraSpinsWon;

                this.flowPresentationTimeline.add(this.fsContainer.unlockSecondStageReels(), 0);
                this.flowPresentationTimeline.add(
                    this.fsLeftCounter.getUpdateCounterTextAnimation(extraSpinsWon, false),
                    `<`
                );
                // this.flowPresentationTimeline.call(
                //     () => {
                //         this.keypad.setZeroBetSpinCounter(zeroBetSpins);
                //     },
                //     undefined,
                //     `>`
                // );

                const dataFullReels = Apocalypse.model.data.fullReels;
                const fullReelsTl = gsap.timeline({
                    delay: 0.2,
                });

                dataFullReels.forEach((_, reelIndex) => {
                    if (dataFullReels[reelIndex]) {
                        extraSpinsWon++;
                        fullReelsTl.add(
                            addExtraSpin(this.fsContainer, reelIndex, extraSpinsWon, false)
                        );
                        // fullReelsTl.call(
                        //     () => {
                        //         this.keypad.setZeroBetSpinCounter(extraSpinsWon);
                        //     },
                        //     undefined,
                        //     `>`
                        // );
                    }
                });

                this.flowPresentationTimeline.add(fullReelsTl, `>`);

                // this.flowPresentationTimeline.call(
                //     () => {
                //         this.keypad.setZeroBetSpinCounter(this.model.data.secondStageFsLeft);
                //     },
                //     undefined,
                //     `>`
                // );
            }

            if (Apocalypse.model.data.secondStage) {
                this.flowPresentationTimeline.add(this.fsContainer.movePersistentReels());
            }
        }

        this.flowPresentationTimeline.resume();
    }

    protected finishPresentation() {
        const hasFullReels =
            Apocalypse.model.data.isRestoreState === false &&
            Apocalypse.model.gameData.fullReels.filter((isTrue) => isTrue).length > 0;

        if (
            Apocalypse.model.data.firstStage &&
            Apocalypse.model.data.freespinsLeft === 0 &&
            hasFullReels
        ) {
            gsap.delayedCall(0.2, () => {
                super.finishPresentation();
            });
        } else {
            super.finishPresentation();
        }
    }
}
